import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@material-ui/core";
import { useGroopitApiContext, useGroupContext } from "groopit-js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NamedGroopitFilterDisplayDTO } from "groopit-js/client";
import useLatestRef from "../react/react-use/useLatestRef";
import { useFilterContext } from "./State/FilterContext";
import { useFilterDispatchContext } from "./State/FilterDispatchContext";
import { GetDateRange } from "./DatePicker";
import {
  GroopitFilter,
  isDefaultFilter,
  UnnamedGroopitFilterDisplayDTO,
} from "./GroopitFilterDTO";
import {
  createSavedFiltersDeleteAction,
  createSavedFiltersReplaceAction,
} from "./State/SavedFiltersActions";
import { createFilterReplaceFilterAction } from "./State/FilterActions";
import { EditIcon } from "../Icons/EditIcon";
import { SaveFilterDialog } from "./SaveFilterDialog";
import { PublishIcon } from "../Icons/PublishIcon";
import { DeleteIcon } from "../Icons/DeleteIcon";
import equal from "fast-deep-equal";

interface SavedFilterOptionProps {
  option: GroopitFilter;
  onSelect: (value: GroopitFilter) => void;
  onRename: (filter: NamedGroopitFilterDisplayDTO) => void;
  onPublish: (filter: NamedGroopitFilterDisplayDTO) => void;
  onDelete: (id: number) => void;
}

const SavedFilterOption = (props: SavedFilterOptionProps) => {
  const { option, onSelect, onRename, onPublish, onDelete } = props;
  const { user_id, owner } = useGroupContext();
  const { filter } = useFilterContext();
  // We set this based on hover so touch devices should default to true
  const [displayActions, setDisplayActions] = useState(
    window.matchMedia("(pointer: coarse)").matches,
  );

  const optionLabel = useMemo(() => {
    return option instanceof NamedGroopitFilterDisplayDTO
      ? option.name
      : "Most recent focus";
  }, [option]);

  const isActiveFilter = useMemo(() => {
    if (
      filter instanceof NamedGroopitFilterDisplayDTO &&
      option instanceof NamedGroopitFilterDisplayDTO
    ) {
      return filter.id === option.id;
    } else {
      return false;
    }
  }, [filter, option]);

  const isFilterModified = useMemo(() => {
    if (!isActiveFilter) {
      return false;
    } else {
      return !equal(option, filter);
    }
  }, [isActiveFilter, option, filter]);

  return (
    <li
      className="point-on-hover flex h-8 items-center hover:bg-app-gray-12"
      onMouseEnter={() => setDisplayActions(true)}
      onMouseLeave={() => setDisplayActions(false)}
    >
      <div className="flex-grow space-x-1 flex items-center overflow-hidden">
        <span
          className={`truncate ${
            isActiveFilter ? "font-bold text-app-teal" : ""
          } ${isFilterModified ? "italic" : ""}`}
          onClick={() => onSelect(option)}
        >
          {optionLabel}
          {option instanceof NamedGroopitFilterDisplayDTO && option.isPublished
            ? " (published)"
            : ""}
        </span>
      </div>
      {displayActions && option instanceof NamedGroopitFilterDisplayDTO && (
        <>
          {(owner || option.creator.id === user_id) && (
            <IconButton
              className="MuiAutocomplete-option-showOnFocus"
              size="small"
              onClick={(e) => {
                onRename(option);
                e.stopPropagation();
              }}
            >
              <EditIcon />
            </IconButton>
          )}
          {owner && !option.isPublished && (
            <IconButton
              className="MuiAutocomplete-option-showOnFocus"
              size="small"
              onClick={(e) => {
                onPublish(option);
                e.stopPropagation();
              }}
            >
              <PublishIcon />
            </IconButton>
          )}
          {(owner || option.creator.id === user_id) && (
            <IconButton
              className="MuiAutocomplete-option-showOnFocus"
              size="small"
              onClick={(e) => {
                // We checked this above
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                onDelete(option.id!);
                e.stopPropagation();
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </>
      )}
    </li>
  );
};

export const SavedFilterPicker = () => {
  const api = useLatestRef(useGroopitApiContext());
  const { group } = useGroupContext();
  const { filter, lastUsedFilter, savedFilters } = useFilterContext();
  const { filterDispatcher, savedFiltersDispatcher } =
    useFilterDispatchContext();

  const [selectedFilter, setSelectedFilter] = useState<GroopitFilter | null>(
    null,
  );
  const [filterMenuOptions, setFilterMenuOptions] = useState<GroopitFilter[]>(
    [],
  );
  const [displayRenameDialog, setDisplayRenameDialog] =
    useState<NamedGroopitFilterDisplayDTO>();
  const [displayPublishDialog, setDisplayPublishDialog] =
    useState<NamedGroopitFilterDisplayDTO>();
  const [displayDeleteConfirmationDialog, setDisplayDeleteConfirmationDialog] =
    useState<NamedGroopitFilterDisplayDTO>();

  const deleteFilter = useCallback(
    (filter?: number) => {
      if (filter === undefined) {
        // This isn't really possible but Typescript can't
        // figure it out so just handle to get rid of the
        // warning
        return;
      }

      api.current.groop
        .deleteGroopitFilter(group.id, filter)
        .then(() => {
          savedFiltersDispatcher(createSavedFiltersDeleteAction(filter));
          if (
            selectedFilter instanceof NamedGroopitFilterDisplayDTO &&
            selectedFilter.id === filter
          ) {
            setSelectedFilter(null);
          }
        })
        .catch((e) => {
          console.error(e);
        });
    },
    [api, group.id, savedFiltersDispatcher, selectedFilter],
  );

  useEffect(() => {
    // If we're picking a saved filter we need to update the date
    // ranges to reflect the current time
    if (selectedFilter) {
      let dateRange = selectedFilter.filter.dateRange;
      if (dateRange) {
        dateRange = GetDateRange(
          dateRange.id,
          dateRange.startDate,
          dateRange.endDate,
        );
      }
      selectedFilter.filter.dateRange = dateRange;
      filterDispatcher(createFilterReplaceFilterAction(selectedFilter));
    }
  }, [selectedFilter, filterDispatcher]);

  useEffect(() => {
    if (isDefaultFilter(filter)) {
      setSelectedFilter(null);
    } else {
      setSelectedFilter(filter);
    }
  }, [filter]);

  useEffect(() => {
    api.current.groop
      .getGroopitFilters(group.id)
      .then((savedFilters) =>
        savedFiltersDispatcher(createSavedFiltersReplaceAction(savedFilters)),
      )
      .catch((e) => {
        console.error(e);
      });
  }, [group.id, api, savedFiltersDispatcher]);

  useEffect(() => {
    const options: GroopitFilter[] = [];

    if (lastUsedFilter) {
      options.push(
        new UnnamedGroopitFilterDisplayDTO({ filter: lastUsedFilter }),
      );
    }

    if (savedFilters) {
      options.push(...savedFilters);
    }

    setFilterMenuOptions(options);
  }, [savedFilters, lastUsedFilter]);

  return (
    <div className="p-4">
      {filterMenuOptions.length ? (
        <>
          <ul>
            {filterMenuOptions.map((option) => (
              <SavedFilterOption
                key={
                  option instanceof NamedGroopitFilterDisplayDTO
                    ? option.id
                    : -1
                }
                option={option}
                onSelect={setSelectedFilter}
                onRename={setDisplayRenameDialog}
                onPublish={setDisplayPublishDialog}
                onDelete={() =>
                  setDisplayDeleteConfirmationDialog(
                    option as NamedGroopitFilterDisplayDTO,
                  )
                }
              />
            ))}
          </ul>
          {Boolean(displayRenameDialog) && (
            <SaveFilterDialog
              filter={displayRenameDialog as NamedGroopitFilterDisplayDTO}
              mode="rename"
              open
              onClose={() => setDisplayRenameDialog(undefined)}
            />
          )}
          {Boolean(displayPublishDialog) && (
            <SaveFilterDialog
              filter={displayPublishDialog as NamedGroopitFilterDisplayDTO}
              mode="publish"
              open
              onClose={() => setDisplayPublishDialog(undefined)}
            />
          )}
        </>
      ) : (
        <div className="italic">No saved filters</div>
      )}
      {!savedFilters && <CircularProgress />}
      <Dialog
        open={Boolean(displayDeleteConfirmationDialog)}
        onClose={() => setDisplayDeleteConfirmationDialog(undefined)}
        aria-labelledby="delete-confirmation-title"
        aria-describedby="delete-confirmation-description"
      >
        <DialogTitle id="delete-confirmation-title">Delete filter</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-confirmation-description">
            {displayDeleteConfirmationDialog?.isPublished ? (
              <span>
                Are you sure you want to delete this saved filter? It is
                published to all users in your group.
              </span>
            ) : (
              <span>Are you sure you want to delete this saved filter?</span>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="default"
            variant="outlined"
            onClick={(event) => {
              setDisplayDeleteConfirmationDialog(undefined);
              event.stopPropagation();
            }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={(event) => {
              deleteFilter(displayDeleteConfirmationDialog?.id);
              setDisplayDeleteConfirmationDialog(undefined);
              event.stopPropagation();
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
